/* eslint-disable no-param-reassign */
import classnames from "classnames";
import ProductFilterSummarValue from "component/product/ProductFilterSummaryValue";
import ResultLabel from "component/product/ResultLabel";
import { FILTER_TYPE } from "constant/product-filter.action";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import qs from "query-string";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { TIER_MAKE_MODEL_PAGES, TIER_MAKE_PAGES, TIER_PAGES } from "util/product/product-constants";
import { FILTER_LABEL_MAP } from "../../../modules/category/constants/filter-constants";
import { isArray } from "../../../util/helper/isArray";
import { removeItemArray } from "../../../util/helper/removeItemArray";
import { capitalize } from "../../../util/lodash/capitalize";
import { getHrefAndPath } from "../../../util/url/getHrefAndPath";
import ClearAllFilter from "./ClearAllFilter";
import { filterRenderKeys } from "./filterRenderKeys";
import styles from "./product-filter-summary.module.scss";

function CrossIcon() {
  return (
    <i className={`fa fa-times-circle p-l-5 ${styles.cross_icon}`} />
  );
}

function ProductFilterSummary(props: any) {
  const {
    appliedFilters, filterOptions,
    tiersWithUrls, renderLabelProps, nbcFilterableAttributes = [],
    showClearAll = true,
  } = props;

  const VALID_NBC_FILTERS = nbcFilterableAttributes.map((el) => el.attributeName);
  const renderKeys = filterRenderKeys(appliedFilters, VALID_NBC_FILTERS);
  const { urlInformation } = useSelector((state: RootState) => state.utils);
  const { selectedVehicle } = useSelector((store: RootState) => store.garage);
  const { t } = useTranslation("category");
  const router = useRouter();

  function removeNewPageFilter(filterKey: any, item: any) {
    const query = qs.parse(location.search);
    const productFilters = { ...query, ...urlInformation };
    let pagePath = location.pathname;
    let queryString;
    if (Array.isArray(productFilters[filterKey])) {
      // when filterKey is array delete single item from array.
      const items = productFilters[filterKey].slice(0);
      removeItemArray(items, item);
      queryString = qs.stringify({
        ...query,
        pathname: undefined,
        [filterKey]: items,
      });
    } else {
      const categoryFilters = [
        FILTER_TYPE.TIER0,
        FILTER_TYPE.TIER1,
        FILTER_TYPE.TIER2,
        FILTER_TYPE.TIER3,
      ];
      const categoryFilterIndex = {
        [FILTER_TYPE.TIER0]: 0,
        [FILTER_TYPE.TIER1]: 1,
        [FILTER_TYPE.TIER2]: 2,
        [FILTER_TYPE.TIER3]: 3,
      };
      if (categoryFilters.indexOf(filterKey) !== -1) {
        let findCategory = false;
        const filtered = categoryFilters.filter((key) => {
          if (filterKey === key) {
            findCategory = true;
          }
          return findCategory;
        });
        if (tiersWithUrls) {
          let indexOfTier = tiersWithUrls.findIndex((tierItem: any) => tierItem.tier.toLowerCase() === item.toLowerCase());
          // If tiers have same url, it's length will be less than 4
          if (tiersWithUrls.length < categoryFilterIndex[filterKey] + 1) {
            indexOfTier = tiersWithUrls.length;
          }
          // navigate to previous tier url.
          if (indexOfTier > 0 && tiersWithUrls[indexOfTier - 1]) {
            pagePath = tiersWithUrls[indexOfTier - 1].url;
            const destinationTier = tiersWithUrls[indexOfTier - 1];
            const { year, make, model } = selectedVehicle || {};
            let vehicleQueryFilters = {};
            if (TIER_PAGES.includes(destinationTier.pageType)) {
              vehicleQueryFilters = { year, make, model };
            } else if (TIER_MAKE_MODEL_PAGES.includes(destinationTier.pageType)) {
              vehicleQueryFilters = { year };
            } else if (TIER_MAKE_PAGES.includes(destinationTier.pageType)) {
              vehicleQueryFilters = { year, model };
            }
            queryString = qs.stringify({
              ...query,
              ...vehicleQueryFilters,
              pathname: undefined,
              [filterKey]: undefined,
              ...filtered.reduce((result, val) => {
                result[val] = undefined;
                return result;
              }, {}),
            });
          } else {
            queryString = qs.stringify({
              ...query,
              pathname: undefined,
              ...filtered.reduce((result, val) => {
                result[val] = undefined;
                return result;
              }, {}),
            });
          }
        }
      } else {
        // when filterKey is not array delete that key.
        queryString = qs.stringify({
          ...query,
          pathname: undefined,
          [filterKey]: undefined,
          scroll: undefined,
        });
      }
    }
    const { href, asPath } = getHrefAndPath({ queryString, asPath: pagePath });
    router.push(href, asPath);
  }

  function removeFilter(filterKey: any, item: any) {
    removeNewPageFilter(filterKey, item);
  }

  function renderFilterKey(filterKey: any) {
    const filterItem = appliedFilters[filterKey];
    if (isArray(filterItem)) {
      // there might be bug here, regarding reconciliation and key.
      // ex: https://dev.partsavatar.ca/tire-chains?availabilities=In%20Stock&availabilities=Out%20of%20Stock
      return filterItem.map((item: any) => renderItem(filterKey, item));
    }
    return renderItem(filterKey, filterItem);
  }

  function renderItem(filterKey: any, item: any) {
    const nbcObj = VALID_NBC_FILTERS.includes(filterKey) && nbcFilterableAttributes.find((el) => el.attributeName === filterKey);
    const nbcLabel = nbcObj?.attributeTitle;
    return (
      <div
        role="button"
        tabIndex="-1"
        key={`${filterKey}-${item}`}
        className={`btn ${styles.filter_item} ${styles.filter_item_card}`}
        onClick={() => removeFilter(filterKey, item)}
      >

        {nbcLabel || t(FILTER_LABEL_MAP[filterKey]) || capitalize(filterKey)}
        :
        <ProductFilterSummarValue filterKey={filterKey} item={item} filterOptions={filterOptions} capitalizeValue={!nbcObj} />

        <span className="p-l-3" />

        <CrossIcon />
      </div>
    );
  }

  return (
    <div data-testid="product-filter-summary" className={classnames(styles.product_filter_summary, "p-b-5", { empty: renderKeys.length === 0 })}>
      <div className={styles.filter_items}>
        {renderKeys.map(renderFilterKey)}
        <HideIf show={showClearAll}>
          <ClearAllFilter
            className={`${styles.clearall_item} ${styles.filter_item_card}`}
            appliedFilters={appliedFilters}
            nbcFilterableAttributes={nbcFilterableAttributes}
          />
        </HideIf>

        <div className="clearfix" />
      </div>
      <ResultLabel {...renderLabelProps} />
    </div>
  );
}

export default ProductFilterSummary;
